import { api } from './api.js';

/**
 * Opciones de catalogo para los selectores.
 *
 * Nada que se elija de una lista vuelve a ser texto libre, asi que casi todo
 * formulario necesita las claves vigentes de uno o varios catalogos. Se
 * cargan una vez por sesion y se comparten: cinco pantallas que piden
 * `ciudades` producen UNA peticion, no cinco.
 *
 * Lo que se guarda es la PROMESA, no el resultado. Si se guardara el
 * resultado, dos pantallas que montan al mismo tiempo dispararian las dos su
 * peticion antes de que ninguna termine, que es el caso que de verdad ocurre
 * cuando una pantalla tiene varios selectores.
 */

export interface Opcion {
  /** El id tecnico. Viaja al servidor y NO se muestra. */
  id: string;
  /** Lo que la persona lee: clave y nombre. */
  etiqueta: string;
  /** Un catalogo desactivado se sigue leyendo pero ya no se ofrece. */
  activo: boolean;
}

interface FilaCatalogo {
  id: string;
  clave?: string;
  folio?: string;
  nombre?: string;
  determinante?: string;
  activo?: boolean;
  estado?: string;
}

/**
 * Un catalogo cargado, y si lo que llego es TODO.
 *
 * `parcial` es la pieza que faltaba. El motor pagina y topa en 200, asi que
 * un catalogo mas grande llega cortado y hasta ahora nadie se enteraba: el
 * selector ofrecia las primeras 200 por orden alfabetico y lo demas
 * simplemente no existia. Con el catalogo de ciudades de la republica —2815
 * municipios— eso significa que no se puede dar de alta un sitio en
 * Zacatecas, y la pantalla no dice nada: parece que esa ciudad no existe.
 *
 * Un selector que calla lo que le falta es peor que uno que tarda.
 */
export interface Catalogo {
  lista: Opcion[];
  /** Cuantos hay en total, segun el servidor. */
  total: number;
  /** `true` si el servidor tiene mas de los que mando. */
  parcial: boolean;
}

/* El tope del motor. Pedir mas no trae mas —lo recorta en 200— asi que el
   numero vive aqui y no disperso en cada llamada. */
const PAGINA = 200;

const cache = new Map<string, Promise<Catalogo>>();

/** Como se lee una fila de catalogo: clave o folio, mas el nombre. */
function etiquetar(f: FilaCatalogo): string {
  const codigo = f.clave ?? f.folio ?? f.determinante ?? '';
  const nombre = f.nombre ?? '';
  if (codigo && nombre) return `${codigo} · ${nombre}`;
  return codigo || nombre || '(sin nombre)';
}

/**
 * Carga las opciones de una entidad del motor. `ruta` es el segmento, no la
 * URL completa: `clientes`, `ciudades`, `contratos-doc`.
 */
export function opciones(ruta: string): Promise<Opcion[]> {
  return catalogo(ruta).then((c) => c.lista);
}

/**
 * Lo mismo, pero diciendo si el catalogo viene COMPLETO.
 *
 * Es la misma peticion y la misma cache que `opciones()`: dos funciones con
 * dos caches darian dos respuestas sobre el mismo catalogo, y la que se
 * olvidara de invalidar seria la equivocada.
 */
export function catalogo(ruta: string): Promise<Catalogo> {
  const guardada = cache.get(ruta);
  if (guardada) return guardada;

  const promesa = api
    .obtener<FilaCatalogo[]>(`/v1/${ruta}?limite=${PAGINA}`)
    .then(({ datos, meta }) => {
      const lista = datos.map((f) => ({
        id: f.id,
        etiqueta: etiquetar(f),
        activo: f.activo ?? true,
      }));
      /* `total` lo manda el motor. Si no viniera, se supone completo: es
         mejor que inventar un corte, porque un catalogo chico marcado como
         parcial mandaria a la persona a buscar en una lista de ocho. */
      const total = meta.total ?? lista.length;
      return { lista, total, parcial: total > lista.length };
    })
    /* Una carga fallida NO se queda cacheada: si se guardara, el selector
       quedaria vacio para el resto de la sesion por un error de red de un
       segundo, sin modo de reintentar salvo recargando la pagina. */
    .catch((e: unknown) => {
      cache.delete(ruta);
      throw e;
    });

  cache.set(ruta, promesa);
  return promesa;
}

/**
 * Busca en el catalogo CONTRA EL SERVIDOR.
 *
 * Para los catalogos que no caben en una pagina. No se cachea a proposito:
 * cada tecleo es una pregunta distinta y guardar la respuesta de "zac" para
 * cuando alguien escriba "zacatecas" solo serviria para dar resultados
 * viejos.
 *
 * El motor busca sobre sus columnas `buscables` —clave y nombre en todos los
 * catalogos—, asi que esto no necesita saber nada de la entidad.
 */
export function buscarOpciones(ruta: string, q: string): Promise<Opcion[]> {
  return api
    .obtener<FilaCatalogo[]>(`/v1/${ruta}?limite=${PAGINA}&q=${encodeURIComponent(q)}`)
    .then(({ datos }) =>
      datos.map((f) => ({ id: f.id, etiqueta: etiquetar(f), activo: f.activo ?? true })),
    );
}

/**
 * Olvida lo cargado. Se llama al guardar una entidad que a su vez es
 * catalogo de otra: dar de alta un cliente y no verlo en el selector de
 * sitios hasta recargar es el tipo de detalle que hace que la gente deje de
 * confiar en la pantalla.
 */
export function olvidarOpciones(ruta?: string): void {
  if (ruta === undefined) cache.clear();
  else cache.delete(ruta);
}
